import { Component, Input } from "@angular/core";
import {
  ExtraProductInformationElement,
  ExtraProductInformationItem
} from "src/app/data-models/extra-product-information.model";

/**
 * Presentational block with product facts delivered by the backend (part number, lead time, shipping data).
 * Renders nothing when there is nothing to show, so a caller can always place it in the template.
 */
@Component({
  selector: "app-extra-product-information",
  templateUrl: "./extra-product-information.component.html",
  styleUrls: ["./extra-product-information.component.scss"]
})
export class ExtraProductInformationComponent {

  @Input() items: ExtraProductInformationItem[] = [];
  /** Shipping data per element; the same element may appear more than once. */
  @Input() elements: ExtraProductInformationElement[] = [];

  public readonly missingValue = "-";
  /** An unexpectedly long backend label is shortened so it cannot take over the layout; the full text stays in the title. */
  public readonly limits = { name: 50, value: 300, elementName: 80, elementDimensions: 120 };

  public get hasItems(): boolean {
    return this.items?.length > 0;
  }

  public get hasElements(): boolean {
    return this.elements?.length > 0;
  }

  /** A column without a single value carries nothing and is left out. */
  public get hasElementDimensions(): boolean {
    return this.elements?.some((element) => !!element.dimensions);
  }

  public get hasElementWeights(): boolean {
    return this.elements?.some((element) => !!element.weight);
  }

  /** The heading names only the columns that are shown. */
  public get elementsHeading(): string {
    if (this.hasElementDimensions && this.hasElementWeights) return "Shipping dimensions and weight by element";

    return this.hasElementDimensions ? "Shipping dimensions by element" : "Shipping weight by element";
  }

  public shorten(text: string, limit: number): string {
    return text.length > limit ? `${text.slice(0, limit - 1).trimEnd()}\u2026` : text;
  }

  public fullTextIfShortened(text: string, limit: number): string | null {
    return text.length > limit ? text : null;
  }

  /**
   * Narrow screens wrap the dimensions only after an "x", so no value parts from its axis: 68" W x / 40" D x / 36" H.
   * Any other text wraps as usual, also text that follows the dimensions.
   */
  public breakableDimensions(dimensions: string): string {
    if (!/\s[x\u00D7]\s/i.test(dimensions)) return dimensions;

    return dimensions
      .replace(/(\d\S*)\s+([a-z])(?![a-z])/gi, "$1\u00A0$2")
      .replace(/\s+([x\u00D7])(?=\s)/gi, "\u00A0$1");
  }

  public trackBySlug(_index: number, item: ExtraProductInformationItem): string {
    return item.slug;
  }
}
